import React from "react";
import PropTypes from "prop-types";
import { Skeleton } from "./Skeleton";
import "./AnalysisSkeleton.css";

const Caption = () => <div className="akv-analysis-skeleton-caption"><Skeleton variant="title" /><Skeleton variant="text" /></div>;

/**
 * Espace de travail « Analyse des enrichissements » : le bandeau de filtres
 * est déjà rendu par la vue, le plan des sections par le shell. Le squelette
 * reprend la section Bilan telle qu'elle s'affiche : six indicateurs, deux
 * palmarès côte à côte, deux surfaces de synthèse, puis le tableau par espèce.
 */
const EnrichmentSkeleton = () => (
  <div className="akv-analysis-skeleton akv-analysis-skeleton--enrichment" data-ak-v2-component="skeleton-layout" data-variant="analysis-enrichment" aria-hidden="true">
    <div className="akv-analysis-skeleton-main">
      <div className="akv-analysis-skeleton-figure" data-skeleton-zone="kpis">
        <Caption />
        <div className="akv-analysis-skeleton-kpis">{[0, 1, 2, 3, 4, 5].map((item) => <div key={item}><Skeleton variant="text" /><Skeleton variant="title" /><Skeleton variant="text" /></div>)}</div>
      </div>
      <div className="akv-analysis-skeleton-pair" data-skeleton-zone="rankings">
        {[0, 1].map((surface) => (
          <div className="akv-analysis-skeleton-figure" key={surface}>
            <Caption />
            <div className="akv-analysis-skeleton-ranks">{[0, 1, 2, 3, 4].map((row) => <div key={row}><Skeleton variant="avatar" /><div><Skeleton variant="text" /><Skeleton variant="text" /></div><Skeleton variant="text" /></div>)}</div>
          </div>
        ))}
      </div>
      <div className="akv-analysis-skeleton-pair" data-skeleton-zone="breakdowns">
        {[0, 1].map((surface) => (
          <div className="akv-analysis-skeleton-figure" key={surface}>
            <Caption />
            <div className="akv-analysis-skeleton-bars">{[0, 1, 2, 3].map((row) => <div key={row}><Skeleton variant="text" /><Skeleton variant="block" /></div>)}</div>
          </div>
        ))}
      </div>
      <div className="akv-analysis-skeleton-figure" data-skeleton-zone="table">
        <Caption />
        <div className="akv-analysis-skeleton-rows">{[0, 1, 2, 3, 4].map((row) => <div key={row}><Skeleton variant="text" /><Skeleton variant="text" /><Skeleton variant="text" /><Skeleton variant="text" /></div>)}</div>
      </div>
    </div>
  </div>
);

/** Content only: the route owns the real heading and the shell owns navigation. */
export function AnalysisSkeleton({ kind = "scan" }) {
  if (kind === "enrichment") return <EnrichmentSkeleton />;
  const metrics = <div className="akv-analysis-skeleton-metrics" data-skeleton-zone="metrics">{[0, 1, 2, 3].map((item) => <div key={item}><Skeleton variant="text" /><Skeleton variant="title" /></div>)}</div>;
  return (
    <div className="akv-analysis-skeleton" data-ak-v2-component="skeleton-layout" data-variant={`analysis-${kind}`} aria-hidden="true">
      <div className="akv-analysis-skeleton-main">
        {kind === "focal" ? metrics : <div className="akv-analysis-skeleton-controls" data-skeleton-zone="controls">{[0, 1, 2].map((item) => <div key={item}><Skeleton variant="text" /><Skeleton variant="button" /></div>)}</div>}
        <div className="akv-analysis-skeleton-figure" data-skeleton-zone="analysis-workspace">
          <div className="akv-analysis-skeleton-caption"><Skeleton variant="title" /><Skeleton variant="text" /></div>
          {kind === "focal" ? <div className="akv-analysis-skeleton-summary">{[0, 1, 2, 3, 4].map((item) => <div key={item}><Skeleton variant="text" /><Skeleton variant="block" /></div>)}</div> : <div className={`akv-analysis-skeleton-plot akv-analysis-skeleton-plot--${kind}`}><Skeleton variant="block" /></div>}
          {kind === "scan" ? <div className="akv-analysis-skeleton-legend"><Skeleton variant="text" /><Skeleton variant="text" /></div> : null}
        </div>
        {kind === "questionnaire" ? metrics : null}
      </div>
      <div className="akv-analysis-skeleton-filters" data-skeleton-zone="filters">
        <Skeleton variant="title" />
        {[0, 1, 2, 3, 4].map((item) => <div key={item}><Skeleton variant="text" /><Skeleton variant="button" /></div>)}
      </div>
    </div>
  );
}
AnalysisSkeleton.propTypes = { kind: PropTypes.oneOf(["scan", "focal", "questionnaire", "enrichment"]) };
